import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Button, Icon } from '@column/column-ui-kit';

import { Flex, Text } from '~/styles';

const StyledButton = styled(Button)`
  padding: 0 10px;
`;

const CounterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 20px;
`;

const NumberContainer = styled.div<{ $width?: number }>`
  position: relative;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  min-width: 1ch;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  text-align: center;
  transition: width 0.1s;
`;

const Sizer = styled.span`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  height: 0;
  white-space: pre;
`;

const AnimatedNumber = styled.div<{
  $direction: 'up' | 'down';
  $type: 'in' | 'out';
  $animate: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 20px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $animate, $direction, $type }) =>
    $animate &&
    ($direction === 'up'
      ? $type === 'in'
        ? css`
            animation: slideInUp 0.3s forwards;
          `
        : css`
            animation: slideOutUp 0.3s forwards;
          `
      : $type === 'in'
        ? css`
            animation: slideInDown 0.3s forwards;
          `
        : css`
            animation: slideOutDown 0.3s forwards;
          `)}

  @keyframes slideInUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes slideOutUp {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-100%);
      opacity: 0;
    }
  }
  @keyframes slideInDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes slideOutDown {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(100%);
      opacity: 0;
    }
  }
`;

interface PageCounterProps {
  current: number;
  total: number;
  direction: 'up' | 'down';
  onPrevious: () => void;
  onNext: () => void;
}

export const PageCounter: React.FC<PageCounterProps> = ({ current, total, direction, onPrevious, onNext }) => {
  // For current
  const [displayed, setDisplayed] = useState(current);
  const [prev, setPrev] = useState<number | null>(null);
  const [numberWidth, setNumberWidth] = useState<number | undefined>(undefined);
  const [hasMounted, setHasMounted] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [animDirection, setAnimDirection] = useState<'up' | 'down'>(direction);

  // For total
  const [displayedTotal, setDisplayedTotal] = useState(total);
  const [prevTotal, setPrevTotal] = useState<number | null>(null);
  const [totalWidth, setTotalWidth] = useState<number | undefined>(undefined);
  const totalTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const sizerRef = useRef<HTMLSpanElement>(null);
  const totalSizerRef = useRef<HTMLSpanElement>(null);

  // Find the largest number string for sizing
  const maxDigits = Math.max(String(current).length, String(total).length);
  const maxNumber = Math.max(Number('9'.repeat(maxDigits)), current, total);

  useLayoutEffect(() => {
    if (sizerRef.current) {
      setNumberWidth(sizerRef.current.offsetWidth);
    }
    if (totalSizerRef.current) {
      setTotalWidth(totalSizerRef.current.offsetWidth);
    }
  }, [maxNumber]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Animate current
  useEffect(() => {
    if (current !== displayed) {
      // Set the animation direction based on the value change, not the prop
      const newDirection = current > displayed ? 'up' : 'down';
      setAnimDirection(newDirection);

      setPrev(displayed);
      setDisplayed(current);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setPrev(null);
      }, 300); // match animation duration
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [current, displayed]);

  // Animate total
  useEffect(() => {
    if (total !== displayedTotal) {
      setPrevTotal(displayedTotal);
      setDisplayedTotal(total);

      if (totalTimeoutRef.current) clearTimeout(totalTimeoutRef.current);
      totalTimeoutRef.current = setTimeout(() => {
        setPrevTotal(null);
      }, 300); // match animation duration
    }
    return () => {
      if (totalTimeoutRef.current) clearTimeout(totalTimeoutRef.current);
    };
  }, [total, displayedTotal]);

  const shouldAnimate = hasMounted && prev !== null;
  const shouldAnimateTotal = hasMounted && prevTotal !== null;

  return (
    <div style={{ marginRight: '-8px' }}>
      <Flex alignItems="center" flexDirection="row">
        <StyledButton
          variant="subtle"
          icon={<Icon.ChevronLeft />}
          onlyIcon
          onClick={onPrevious}
          isDisabled={current === 1}
        />
        <CounterWrapper>
          <NumberContainer $width={numberWidth}>
            {/* Sizer for width calculation */}
            <Sizer ref={sizerRef}>
              <Text color="light" size={14} weight="medium">
                {maxNumber}
              </Text>
            </Sizer>
            {prev !== null && (
              <AnimatedNumber $direction={animDirection} $type="out" $animate={shouldAnimate} key={`out-${prev}`}>
                <Text color="light" size={14} weight="medium">
                  {prev}
                </Text>
              </AnimatedNumber>
            )}
            <AnimatedNumber $direction={animDirection} $type="in" $animate={shouldAnimate} key={`in-${displayed}`}>
              <Text color="light" size={14} weight="medium">
                {displayed}
              </Text>
            </AnimatedNumber>
          </NumberContainer>
          <Text color="lighter" size={14} weight="medium">
            of
          </Text>
          <NumberContainer $width={totalWidth}>
            <Sizer ref={totalSizerRef}>
              <Text color="light" size={14} weight="medium">
                {maxNumber}
              </Text>
            </Sizer>
            {prevTotal !== null && (
              <AnimatedNumber
                $direction="down"
                $type="out"
                $animate={shouldAnimateTotal}
                key={`out-total-${prevTotal}`}
              >
                <Text color="light" size={14} weight="medium">
                  {prevTotal}
                </Text>
              </AnimatedNumber>
            )}
            <AnimatedNumber
              $direction="down"
              $type="in"
              $animate={shouldAnimateTotal}
              key={`in-total-${displayedTotal}`}
            >
              <Text color="light" size={14} weight="medium">
                {displayedTotal}
              </Text>
            </AnimatedNumber>
          </NumberContainer>
        </CounterWrapper>
        <StyledButton
          variant="subtle"
          icon={<Icon.ChevronRight />}
          onlyIcon
          onClick={onNext}
          isDisabled={current === total}
        />
      </Flex>
    </div>
  );
};
